import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Ban, ChevronLeft, KeyRound, ShieldCheck } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { ErrorState, Skeleton, EmptyState } from '@/components/States'
import { useToast } from '@/providers/UiProviders'
import { ApiError } from '@/lib/api'
import {
  fetchApp,
  fetchUsageStats,
  setAppStatus,
  DEVX_STR as S,
  type AppRow,
  type UsageStats,
} from './devApiExt'

/** Bir martalik yuklovchi (Ads naqshi) — abort + reload bilan. */
function useLoad<T>(fn: (s: AbortSignal) => Promise<T>) {
  const [data, setData] = useState<T | null>(null)
  const [err, setErr] = useState<ApiError | null>(null)
  const [tick, setTick] = useState(0)
  useEffect(() => {
    const ac = new AbortController()
    setErr(null)
    fn(ac.signal)
      .then(setData)
      .catch((e) => {
        if (!ac.signal.aborted) setErr(e instanceof ApiError ? e : new ApiError('INTERNAL', String(e), 500))
      })
    return () => ac.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick])
  return { data, err, reload: useCallback(() => setTick((t) => t + 1), []) }
}

function Loading({ n = 6 }: { n?: number }) {
  return (
    <div className="space-y-1.5 p-4">
      {Array.from({ length: n }).map((_, i) => (
        <Skeleton key={i} className="h-7 w-full" />
      ))}
    </div>
  )
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border-default bg-surface-2 p-4">
      <p className="text-xs text-tertiary">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-primary tabular">{value}</p>
    </div>
  )
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between border-t border-border-subtle px-4 py-2.5 first:border-t-0">
      <span className="text-sm text-tertiary">{label}</span>
      <span className="truncate pl-3 text-sm text-primary">{value}</span>
    </div>
  )
}

/* ── A13.02 — Ilova kartasi (apps ro'yxatidan, REAL) ───────────────────── */
export function AppCardScreen() {
  const { id = '' } = useParams()
  const { toast } = useToast()
  const { data, err, reload } = useLoad<AppRow | null>((s) => fetchApp(Number(id), s))
  const [busy, setBusy] = useState(false)

  const changeStatus = async (next: 'active' | 'blocked') => {
    if (!data) return
    setBusy(true)
    try {
      await setAppStatus(data.id, next)
      toast(S.statusChanged, { tone: 'success' })
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <PageHeader
        title={S.appCard}
        screenId="A13.02"
        actions={
          <Link
            to="/developers/apps"
            className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
          >
            <ChevronLeft size={14} />
            {S.back}
          </Link>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        {err ? (
          <ErrorState code={err.code} onRetry={reload} />
        ) : data === null && !err ? (
          <Loading />
        ) : !data ? (
          <EmptyState title={S.appNotFound} icon={<KeyRound size={28} strokeWidth={1.5} />} />
        ) : (
          <div className="mx-auto max-w-2xl space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 className="truncate text-xl font-semibold text-primary">{data.name}</h2>
                <p className="mt-0.5 font-mono text-xs text-tertiary">api_id {data.api_id}</p>
              </div>
              {data.status === 'active' ? (
                <button
                  type="button"
                  onClick={() => changeStatus('blocked')}
                  disabled={busy}
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-danger px-3 py-1.5 text-sm text-white disabled:opacity-50"
                >
                  <Ban size={14} />
                  {S.block}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => changeStatus('active')}
                  disabled={busy}
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-accent px-3 py-1.5 text-sm text-white disabled:opacity-50"
                >
                  <ShieldCheck size={14} />
                  {S.unblock}
                </button>
              )}
            </div>
            <div className="overflow-hidden rounded-lg border border-border-default bg-surface-2">
              <InfoRow label={S.owner} value={data.owner} />
              <InfoRow label={S.status} value={data.status === 'active' ? S.active : S.blocked} />
              <InfoRow label={S.requestsToday} value={(data.requests_today || 0).toLocaleString()} />
              <InfoRow label={S.created} value={data.created_at} />
            </div>
          </div>
        )}
      </div>
    </>
  )
}

/* ── A13.03 — Foydalanish statistikasi (apps ro'yxatidan hosila, REAL) ──── */
export function UsageStatsScreen() {
  const { data, err, reload } = useLoad<UsageStats>(fetchUsageStats)
  return (
    <>
      <PageHeader title={S.usage} screenId="A13.03" />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        {err ? (
          <ErrorState code={err.code} onRetry={reload} />
        ) : !data ? (
          <Loading />
        ) : (
          <div className="mx-auto max-w-4xl">
            <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard label={S.totalApps} value={data.total.toLocaleString()} />
              <StatCard label={S.activeApps} value={data.active.toLocaleString()} />
              <StatCard label={S.blockedApps} value={data.blocked.toLocaleString()} />
              <StatCard label={S.totalRequests} value={data.requests_today.toLocaleString()} />
            </div>
            {data.top.length > 0 ? (
              <div className="mt-4 overflow-hidden rounded-lg border border-border-default">
                <p className="border-b border-border-subtle bg-surface-2 px-4 py-2 text-xs font-medium text-secondary">
                  {S.topApps}
                </p>
                {data.top.map((t, i) => (
                  <div
                    key={i}
                    className={`flex items-center justify-between px-4 py-2 text-sm ${i > 0 ? 'border-t border-border-subtle' : ''}`}
                  >
                    <span className="min-w-0 truncate text-primary">
                      {t.name}
                      <span className="pl-2 text-tertiary">{t.owner}</span>
                    </span>
                    <span className="shrink-0 tabular text-secondary">{t.requests_today.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-4">
                <EmptyState title={S.empty} />
              </div>
            )}
          </div>
        )}
      </div>
    </>
  )
}
